import {
  type MissionAssessmentCapabilityAiGenerationResponseDto,
  type MissionAssessmentCapabilityAiQueryDto,
  MissionAssessmentCoreSelectDto,
  MissionAssessmentCreateDto,
  MissionAssessmentDraftSelectDto,
  type MissionAssessmentEffectAiGenerationResponseDto,
  type MissionAssessmentEffectAiQueryDto,
  MissionAssessmentEffectsSelectDto,
  type MissionAssessmentInterAiGenerationResponseDto,
  type MissionAssessmentInterAiQueryDto,
  MissionAssessmentIntersSelectDto,
  MissionAssessmentListItemDto,
  MissionAssessmentMetadataSelectDto,
  MissionAssessmentSelectDto,
  MissionAssessmentServicesSelectDto,
  MissionAssessmentsSelectDto,
  UserProfileRole,
} from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { queryClient } from './client';
import { queryKeys } from './keys';
import { useGetCurrentUser, useGetUserSelectedCompanyId, useRoleEndpointPrefix } from './users.query';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

const useMissionAssessmentEnabledById = (missionAssessmentId?: string): boolean => {
  const { data: user } = useGetCurrentUser();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const isIntelligence = user?.profile?.role === UserProfileRole.INTELLIGENCE;

  return Boolean(
    Boolean(user) &&
      missionAssessmentId &&
      Boolean(isAuthenticated) &&
      (isExpert || isIntelligence || (isInnovator && Boolean(selectedCompanyId))),
  );
};

/**
 * Hook to determine if the user has access to mission assessment data by technology ID
 */
const useMissionAssessmentEnabledByTechnology = (technologyId?: string): boolean => {
  const { data: user } = useGetCurrentUser();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const isIntelligence = user?.profile?.role === UserProfileRole.INTELLIGENCE;

  return Boolean(
    Boolean(user) &&
      technologyId &&
      Boolean(isAuthenticated) &&
      (isExpert || isIntelligence || (isInnovator && Boolean(selectedCompanyId))),
  );
};

/**
 * Hook to determine if the user has access to mission assessment list data
 */
const useMissionAssessmentListEnabled = (): boolean => {
  const { data: user } = useGetCurrentUser();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;

  return Boolean(
    Boolean(user) && Boolean(isAuthenticated) && (isExpert || (isInnovator && Boolean(selectedCompanyId))),
  );
};

type MissionAssessmentByIdQueryParams = {
  apiGet: ReturnType<typeof useApi>['apiGet'];
  enabled: boolean;
  missionAssessmentId?: string;
  rolePrefix?: string | null;
};

type MissionAssessmentByTechnologyQueryParams = {
  apiGet: ReturnType<typeof useApi>['apiGet'];
  enabled: boolean;
  rolePrefix?: string | null;
  technologyId?: string;
};

type MissionAssessmentListQueryParams = {
  apiGet: ReturnType<typeof useApi>['apiGet'];
  enabled: boolean;
  rolePrefix?: string | null;
};

export const useMissionAssessmentCoreQueryOptions = (missionAssessmentId?: string) => {
  const { apiGet } = useApi({ showToastOnError: false });
  const enabled = useMissionAssessmentEnabledById(missionAssessmentId);
  const rolePrefix = useRoleEndpointPrefix();

  return getMissionAssessmentCoreQueryOptions({
    apiGet,
    enabled: enabled && Boolean(rolePrefix),
    missionAssessmentId,
    rolePrefix,
  });
};

function getMissionAssessmentCoreQueryOptions(params: MissionAssessmentByIdQueryParams) {
  const { apiGet, enabled, missionAssessmentId, rolePrefix } = params;

  return queryOptions<MissionAssessmentCoreSelectDto | null, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!missionAssessmentId || !rolePrefix) {
        throw new Error('missionAssessmentId and rolePrefix are required');
      }

      try {
        return await apiGet<MissionAssessmentCoreSelectDto>({
          path: `/v1/${rolePrefix}/mission-assessments/${missionAssessmentId}`,
        });
      } catch (error) {
        if (error instanceof ApiErrorWrapper && error.errors?.[0]?.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    queryKey:
      enabled && missionAssessmentId
        ? queryKeys.missionAssessments.getCore(missionAssessmentId)
        : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentEffectsQueryOptions = (missionAssessmentId?: string) => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentEnabledById(missionAssessmentId);
  const rolePrefix = useRoleEndpointPrefix();

  return getMissionAssessmentEffectsQueryOptions({
    apiGet,
    enabled: enabled && Boolean(rolePrefix),
    missionAssessmentId,
    rolePrefix,
  });
};

function getMissionAssessmentEffectsQueryOptions(params: MissionAssessmentByIdQueryParams) {
  const { apiGet, enabled, missionAssessmentId, rolePrefix } = params;

  return queryOptions<MissionAssessmentEffectsSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!missionAssessmentId || !rolePrefix) {
        throw new Error('missionAssessmentId and rolePrefix are required');
      }

      return apiGet<MissionAssessmentEffectsSelectDto>({
        path: `/v1/${rolePrefix}/mission-assessments/${missionAssessmentId}/effects`,
      });
    },
    queryKey:
      enabled && missionAssessmentId
        ? queryKeys.missionAssessments.getEffects(missionAssessmentId)
        : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentIntersQueryOptions = (missionAssessmentId?: string) => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentEnabledById(missionAssessmentId);
  const rolePrefix = useRoleEndpointPrefix();

  return getMissionAssessmentIntersQueryOptions({
    apiGet,
    enabled: enabled && Boolean(rolePrefix),
    missionAssessmentId,
    rolePrefix,
  });
};

function getMissionAssessmentIntersQueryOptions(params: MissionAssessmentByIdQueryParams) {
  const { apiGet, enabled, missionAssessmentId, rolePrefix } = params;

  return queryOptions<MissionAssessmentIntersSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!missionAssessmentId || !rolePrefix) {
        throw new Error('missionAssessmentId and rolePrefix are required');
      }

      return apiGet<MissionAssessmentIntersSelectDto>({
        path: `/v1/${rolePrefix}/mission-assessments/${missionAssessmentId}/inters`,
      });
    },
    queryKey:
      enabled && missionAssessmentId
        ? queryKeys.missionAssessments.getInters(missionAssessmentId)
        : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentServicesQueryOptions = (missionAssessmentId?: string) => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentEnabledById(missionAssessmentId);
  const rolePrefix = useRoleEndpointPrefix();

  return getMissionAssessmentServicesQueryOptions({
    apiGet,
    enabled: enabled && Boolean(rolePrefix),
    missionAssessmentId,
    rolePrefix,
  });
};

function getMissionAssessmentServicesQueryOptions(params: MissionAssessmentByIdQueryParams) {
  const { apiGet, enabled, missionAssessmentId, rolePrefix } = params;

  return queryOptions<MissionAssessmentServicesSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!missionAssessmentId || !rolePrefix) {
        throw new Error('missionAssessmentId and rolePrefix are required');
      }

      return apiGet<MissionAssessmentServicesSelectDto>({
        path: `/v1/${rolePrefix}/mission-assessments/${missionAssessmentId}/services`,
      });
    },
    queryKey:
      enabled && missionAssessmentId
        ? queryKeys.missionAssessments.getServices(missionAssessmentId)
        : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentMetadataQueryOptions = (missionAssessmentId?: string) => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentEnabledById(missionAssessmentId);
  const rolePrefix = useRoleEndpointPrefix();

  return getMissionAssessmentMetadataQueryOptions({
    apiGet,
    enabled: enabled && Boolean(rolePrefix),
    missionAssessmentId,
    rolePrefix,
  });
};

function getMissionAssessmentMetadataQueryOptions(params: MissionAssessmentByIdQueryParams) {
  const { apiGet, enabled, missionAssessmentId, rolePrefix } = params;

  return queryOptions<MissionAssessmentMetadataSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!missionAssessmentId || !rolePrefix) {
        throw new Error('missionAssessmentId and rolePrefix are required');
      }
      return apiGet<MissionAssessmentMetadataSelectDto>({
        path: `/v1/${rolePrefix}/mission-assessments/${missionAssessmentId}/metadata`,
      });
    },
    queryKey:
      enabled && missionAssessmentId
        ? queryKeys.missionAssessments.getMetadata(missionAssessmentId)
        : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentVersionsByTechnologyQueryOptions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentEnabledByTechnology(technologyId);
  const rolePrefix = useRoleEndpointPrefix();

  return getMissionAssessmentVersionsByTechnologyQueryOptions({
    apiGet,
    enabled: enabled && Boolean(rolePrefix),
    rolePrefix,
    technologyId,
  });
};

function getMissionAssessmentVersionsByTechnologyQueryOptions(params: MissionAssessmentByTechnologyQueryParams) {
  const { apiGet, enabled, rolePrefix, technologyId } = params;

  return queryOptions<MissionAssessmentListItemDto[], ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!technologyId || !rolePrefix) {
        throw new Error('technologyId and rolePrefix are required');
      }

      return apiGet<MissionAssessmentListItemDto[]>({
        path: `/v1/${rolePrefix}/mission-assessments/technology/${technologyId}/versions`,
      });
    },
    queryKey:
      enabled && technologyId
        ? queryKeys.missionAssessments.getVersionsByTechnology(technologyId)
        : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentAllQueryOptions = () => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentListEnabled();
  const rolePrefix = useRoleEndpointPrefix();
  const isExpert = rolePrefix === 'expert';

  return getMissionAssessmentAllQueryOptions({
    apiGet,
    enabled: enabled && isExpert,
    rolePrefix,
  });
};

function getMissionAssessmentAllQueryOptions(params: MissionAssessmentListQueryParams) {
  const { apiGet, enabled, rolePrefix } = params;

  return queryOptions<MissionAssessmentSelectDto[], ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!rolePrefix) {
        throw new Error('rolePrefix is required');
      }

      const result = await apiGet<{ missionAssessments: MissionAssessmentSelectDto[] }>({
        path: `/v1/${rolePrefix}/mission-assessments/all`,
      });
      return result.missionAssessments;
    },
    queryKey: enabled ? queryKeys.missionAssessments.getAllForExpert : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

export const useMissionAssessmentLatestByCompanyQueryOptions = () => {
  const { apiGet } = useApi();
  const enabled = useMissionAssessmentListEnabled();
  const rolePrefix = useRoleEndpointPrefix();
  const isInnovator = rolePrefix === 'innovator';

  return getMissionAssessmentLatestByCompanyQueryOptions({
    apiGet,
    enabled: enabled && isInnovator,
    rolePrefix,
  });
};

function getMissionAssessmentLatestByCompanyQueryOptions(params: MissionAssessmentListQueryParams) {
  const { apiGet, enabled, rolePrefix } = params;

  return queryOptions<MissionAssessmentSelectDto[], ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!rolePrefix) {
        throw new Error('rolePrefix is required');
      }

      const result = await apiGet<MissionAssessmentsSelectDto>({
        path: `/v1/${rolePrefix}/mission-assessments`,
      });
      return result.missionAssessments;
    },
    queryKey: enabled
      ? queryKeys.missionAssessments.getLatestMissionAssessmentsByCompany
      : queryKeys.placeholders.default,
    staleTime: STALE_TIME,
  });
}

// ============================================================================
// Prefetch hooks
// ============================================================================

/**
 * Hook to prefetch mission assessment data for version navigation.
 * Returns a function that can be called to prefetch data for a specific MA ID.
 */
export const usePrefetchMissionAssessment = () => {
  const queryClient = useQueryClient();
  const { apiGet } = useApi();
  const rolePrefix = useRoleEndpointPrefix();

  const prefetch = useCallback(
    async (missionAssessmentId: string) => {
      if (!rolePrefix) return;

      await queryClient.prefetchQuery(
        getMissionAssessmentCoreQueryOptions({ apiGet, enabled: true, missionAssessmentId, rolePrefix }),
      );
    },
    [apiGet, queryClient, rolePrefix],
  );

  return prefetch;
};

/**
 * Hook to prefetch all section data for a mission assessment.
 * Useful for eager prefetching when hovering over version items.
 */
export const usePrefetchMissionAssessmentFull = () => {
  const queryClient = useQueryClient();
  const { apiGet } = useApi();
  const rolePrefix = useRoleEndpointPrefix();

  const prefetchFull = useCallback(
    async (missionAssessmentId: string) => {
      if (!rolePrefix || !missionAssessmentId) return;

      const baseParams = { apiGet, enabled: true, missionAssessmentId, rolePrefix };

      await Promise.all([
        queryClient.prefetchQuery(getMissionAssessmentCoreQueryOptions(baseParams)),
        queryClient.prefetchQuery(getMissionAssessmentEffectsQueryOptions(baseParams)),
        queryClient.prefetchQuery(getMissionAssessmentIntersQueryOptions(baseParams)),
        queryClient.prefetchQuery(getMissionAssessmentServicesQueryOptions(baseParams)),
        queryClient.prefetchQuery(getMissionAssessmentMetadataQueryOptions(baseParams)),
      ]);
    },
    [apiGet, queryClient, rolePrefix],
  );

  return prefetchFull;
};

// ============================================================================
// Mutations
// ============================================================================

export const useCreateMissionAssessment = () => {
  const { apiPost } = useApi();

  return useMutation<MissionAssessmentSelectDto, ApiErrorWrapper, MissionAssessmentCreateDto>({
    mutationFn: async (data: MissionAssessmentCreateDto) => {
      return await apiPost<MissionAssessmentSelectDto>({
        path: '/v1/expert/mission-assessments',
        body: data,
      });
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentRequests.getMissionAssessmentRequests,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getAllForExpert,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getVersionsByTechnology(variables.technologyId),
      });
    },
    onError: (error) => {
      console.error('Error creating mission assessment:', error);
    },
  });
};

export const useDuplicateMissionAssessmentAsDraft = () => {
  const { apiPost } = useApi();

  return useMutation<
    MissionAssessmentDraftSelectDto,
    ApiErrorWrapper,
    { missionAssessmentId: string; technologyId: string }
  >({
    mutationFn: async (params: { missionAssessmentId: string; technologyId: string }) => {
      if (!params.missionAssessmentId) {
        throw new Error('missionAssessmentId is required to duplicate mission assessment.');
      }
      return await apiPost<MissionAssessmentDraftSelectDto>({
        path: `/v1/expert/mission-assessments/${params.missionAssessmentId}/duplicate`,
        body: {},
      });
    },
    onSuccess: async (_newlyCreatedDraft, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getCore(variables.missionAssessmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getAllForExpert,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessmentDrafts.getDraftsForExpert,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.missionAssessments.getVersionsByTechnology(variables.technologyId),
      });
    },
    onError: (error) => {
      console.error('Error duplicating mission assessment as draft:', error);
    },
  });
};

// ============================================================================
// AI Generation
// ============================================================================

export const useGenerateMissionAssessmentInter = () => {
  const { apiPost } = useApi();

  return useMutation<MissionAssessmentInterAiGenerationResponseDto, ApiErrorWrapper, MissionAssessmentInterAiQueryDto>({
    mutationFn: async (params: MissionAssessmentInterAiQueryDto) => {
      return await apiPost<MissionAssessmentInterAiGenerationResponseDto>({
        body: params,
        path: '/v1/expert/mission-assessments/generate-inter',
      });
    },
  });
};

export const useGenerateMissionAssessmentCapabilities = () => {
  const { apiPost } = useApi();

  return useMutation<
    MissionAssessmentCapabilityAiGenerationResponseDto,
    ApiErrorWrapper,
    MissionAssessmentCapabilityAiQueryDto & { signal?: AbortSignal }
  >({
    mutationFn: async (params) => {
      const { signal, ...body } = params;

      return await apiPost<MissionAssessmentCapabilityAiGenerationResponseDto>({
        body,
        path: '/v1/expert/mission-assessments/generate-capabilities',
        signal,
      });
    },
  });
};

export const useGenerateMissionAssessmentEffect = () => {
  const { apiPost } = useApi();

  return useMutation<
    MissionAssessmentEffectAiGenerationResponseDto,
    ApiErrorWrapper,
    MissionAssessmentEffectAiQueryDto
  >({
    mutationFn: async (params: MissionAssessmentEffectAiQueryDto) => {
      return await apiPost<MissionAssessmentEffectAiGenerationResponseDto>({
        body: params,
        path: '/v1/expert/mission-assessments/generate-effect',
      });
    },
  });
};
